<template>
    <div :class="{ 'mt-2': !title }">
        <fieldset class="flex flex-col gap-y-2 gap-x-4 mt-2" :class="{ 'md:flex-row flex-wrap': horizontal }">
            <legend v-if="!hideTitle" class="mb-2">
                <span class="font-semibold text-para-xs md:text-para-s">
                    {{ title }}
                    <dfn v-if="required" aria-hidden="true" title="required">{{ requiredSymbol }}</dfn>
                </span>
            </legend>
            <div v-for="(label, iValue, index) in options" :key="index" class="flex flex-row items-center">
                <Field
                    v-slot="{ field, errors }"
                    ref="field"
                    v-model="model"
                    :name="name"
                    :label="title"
                    :rules="rules"
                    :disabled="disabled"
                    :type="'checkbox'"
                    :value="iValue"
                    as="div">
                    <input
                        v-bind="field"
                        :id="isSingle ? name : `${name}-${index}`"
                        :name="name"
                        class="absolute opacity-0 text-black-900 text-para-xs md:text-para-s"
                        :type="type"
                        :value="iValue"
                        :aria-invalid="errors.length > 0"
                        :aria-describedby="errors.length > 0 ? `${name}-error` : ''" />
                    <label :for="isSingle ? name : `${name}-${index}`" class="relative" :class="type + '-label'">
                        <span class="text-para-xs pl-5 md:pl-6" v-html="label" />
                    </label>
                </Field>
            </div>
            <ErrorMessage :name="name" v-slot="{ message }">
                <span :id="`${name}-error`" class="text-error text-para-xs mt-1 md:mt-2">
                    {{ message }}
                </span>
            </ErrorMessage>
        </fieldset>
    </div>
</template>

<script lang="ts">
import { ErrorMessage, Field } from 'vee-validate';
import { PropType } from 'vue';

export default {
    components: { ErrorMessage, Field },
    props: {
        name: { type: String },
        title: { type: String },
        description: { type: String },
        defaultValue: { type: String },
        type: { type: String, default: 'text' },
        required: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        horizontal: { type: Boolean, default: false },
        requiredSymbol: { type: String, default: '*' },
        labels: { type: Array as PropType<Array<string>> },
        modelValue: {
            type: [String, Number, Boolean, Array as PropType<Array<string>>],
            default: '',
        },
    },
    emits: ['update:modelValue'],
    data() {
        return {
            model: [],
        };
    },
    computed: {
        rules(): Record<string, unknown> {
            return {
                required: this.required,
            };
        },
        options(): Record<string, string> {
            const result = {};
            if (this.labels) {
                this.labels.forEach((s: string) => {
                    const keyValue = s.split(':');
                    if (keyValue.length === 2) {
                        result[keyValue[1]] = keyValue[0];
                    }
                });
            }
            return result;
        },
        isSingle(): boolean {
            return Object.getOwnPropertyNames(this.options).length == 1;
        },
    },
    watch: {
        async model() {
            if (!this.disabled) {
                if (typeof this.modelValue === 'boolean') {
                    // convert back to boolean if v-model was a boolean
                    this.$emit('update:modelValue', this.model === 'true');
                } else {
                    this.$emit('update:modelValue', this.model);
                }
            }
        },
    },
    mounted(): void {
        if (this.modelValue) {
            this.model = this.modelValue;
        } else if (this.defaultValue) {
            const split: Array<string> = this.defaultValue.split('__');
            split.forEach(s => this.model.push(s));
        }
    },
};
</script>
